import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import QuickActionMenu from '../../components/navigation/QuickActionMenu';


import MetricsCard from './components/MetricsCard';
import CaseCard from './components/CaseCard';
import SubscriptionPanel from './components/SubscriptionPanel';
import UsageChart from './components/UsageChart';

import QuickActions from './components/QuickActions';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { getUserCases, getUserSubscription } from '../../services/caseService';
import { hasCardOnFile } from '../../services/stripeService';
import toast from 'react-hot-toast';
import { getCreditDisplay } from '../../services/testAccountService';

const ClientDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, userRole, signOut } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [cases, setCases] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCardModal, setShowCardModal] = useState(false);
  const [needsCard, setNeedsCard] = useState(false);
  const [noPaymentMethod, setNoPaymentMethod] = useState(false);
  const [creditBreakdown, setCreditBreakdown] = useState(null);
  const [isTestAccount, setIsTestAccount] = useState(false);

  useEffect(() => {
    if (user) {
      loadDashboardData();
      checkCardRequirement();
      checkTestAccount();
    }
  }, [user?.id]);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);

      const [casesResult, subResult] = await Promise.all([
        getUserCases(user?.id),
        getUserSubscription(user?.id),
      ]);

      if (casesResult?.success) {
        setCases(casesResult?.data || []);
      }

      if (subResult?.success) {
        setSubscription(subResult?.data);
      }
    } catch (error) {
      console.error('Load dashboard error:', error);
      toast?.error('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
    }
  };

  const checkTestAccount = async () => {
    try {
      const result = await getCreditDisplay(user?.id);
      if (result?.success && result?.isTestAccount) {
        setIsTestAccount(true);
      }
    } catch (error) {
      console.error('Check test account error:', error);
    }
  };

  const checkCardRequirement = async () => {
    try {
      const result = await hasCardOnFile(user?.id);
      
      // Skip card requirement for test accounts
      if (result?.isTestAccount) {
        setNeedsCard(false);
        setNoPaymentMethod(false);
        return;
      }
      
      if (result?.success && !result?.hasCard && subscription?.plan_type === 'free_trial') {
        setNeedsCard(true);
      }

      if (result?.success && !result?.hasCard) {
        setNoPaymentMethod(true);
      }
    } catch (error) {
      console.error('Check card requirement error:', error);
    }
  };

  const handleCardSuccess = () => {
    setNeedsCard(false);
    toast?.success('Card saved! You can now create your free trial report.');
  };

  const filteredCases = cases?.filter(c => {
    const matchesSearch = !searchTerm || 
      c?.case_number?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      c?.property_address?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || c?.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const statusMap = {
    'draft': 'pending',
    'intake_submitted': 'in-progress',
    'needs_clarification': 'clarification-needed',
    'report_generating': 'in-progress',
    'report_ready': 'completed',
    'archived': 'completed'
  };

  const personaLabels = {
    'insurance-adjuster': 'Insurance Adjuster',
    'plaintiff-attorney': 'Plaintiff Attorney',
    'defense-attorney': 'Defense Attorney',
    'siu-investigator': 'SIU Investigator',
    'subrogation-specialist': 'Subrogation Specialist',
    'prosecutor': 'Prosecutor',
    'fire-marshal': 'Fire Marshal/AHJ',
    'risk-manager': 'Risk Manager',
    'property-owner': 'Property Owner/Insured'
  };

  const formattedCases = filteredCases?.map(c => ({
    caseNumber: c?.case_number,
    caseName: `${c?.allegation_type?.replace('-', ' ')} - ${c?.property_address}`,
    submissionDate: new Date(c?.created_at)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }),
    status: statusMap?.[c?.status] || 'pending',
    persona: personaLabels?.[c?.persona] || c?.persona,
    expertAssigned: null,
    progress: c?.status === 'report_ready' ? 100 : c?.status === 'report_generating' ? 75 : c?.status === 'needs_clarification' ? 50 : 25,
    caseId: c?.id
  }));

  const totalCredits = (subscription?.trial_credits || 0) + (subscription?.paid_credits || 0) + (subscription?.monthly_credits || 0);

  const subscriptionData = subscription ? {
    planType: subscription?.plan_type?.replace('_', '-'),
    creditsRemaining: totalCredits,
    billingPeriod: subscription?.billing_period_start && subscription?.billing_period_end
      ? `${new Date(subscription?.billing_period_start)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${new Date(subscription?.billing_period_end)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
      : 'N/A',
    monthlyCost: subscription?.plan_type === 'monthly' ? 250 : subscription?.plan_type === 'annual' ? 500 : 0,
    nextBillingDate: subscription?.billing_period_end
      ? new Date(subscription?.billing_period_end)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      : null
  } : null;

  const completedCases = cases?.filter(c => c?.status === 'report_ready')?.length;
  const inProgressCases = cases?.filter(c => ['intake_submitted', 'needs_clarification', 'report_generating']?.includes(c?.status))?.length;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar userRole="client" />
      <QuickActionMenu userRole="client" />
      <div className="lg:ml-60 min-h-screen">
        <header className="sticky top-0 z-40 bg-card border-b border-border px-4 md:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  const isCollapsed = document.body.classList.toggle('sidebar-collapsed');
                  localStorage.setItem('sidebarCollapsed', isCollapsed.toString());
                }}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                aria-label="Toggle sidebar"
              >
                <Icon name="PanelLeft" size={24} />
              </button>
              <div>
                <h1 className="text-2xl md:text-3xl font-semibold text-foreground">Dashboard</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Welcome back! Here's your fire forensics overview.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <NotificationCenter userRole="client" />
              <Button
                variant="default"
                iconName="FilePlus"
                iconPosition="left"
                onClick={() => navigate('/new-case-creation-wizard')}
                className="hidden md:flex"
              >
                New Case
              </Button>
              <Button
                variant="outline"
                iconName="LogOut"
                iconPosition="left"
                onClick={async () => { await signOut(); navigate('/login'); }}
              >
                Logout
              </Button>
            </div>
          </div>
        </header>

        {/* Payment Method Banner */}
        {noPaymentMethod && !isTestAccount && (
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 pt-6">
            <div className="bg-warning/10 border border-warning rounded-lg p-4 md:p-6">
              <div className="flex items-start gap-4">
                <Icon name="AlertTriangle" className="w-6 h-6 text-warning flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground mb-2">
                    Action Required: Set Up a Payment Method
                  </h3>
                  <p className="text-muted-foreground mb-1">
                    You need a payment method on file to submit cases and purchase credits.
                  </p>
                  <p className="text-muted-foreground mb-4 text-sm">
                    Add a card now to unlock full access to the platform — case submissions, expert assignments, and report generation all require a valid payment method.
                  </p>
                  <Button
                    onClick={() => navigate('/client-dashboard/financial?tab=payment-method')}
                    iconName="CreditCard"
                  >
                    Set Up Payment Method
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        <main className="px-4 md:px-6 lg:px-8 py-6 md:py-8 space-y-8">
          {/* Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricsCard
              title="Total Cases"
              value={formattedCases?.length}
              icon="FileText"
              trendValue="+2 this month"
              trendUp={true}
            />
            <MetricsCard
              title="Completed Reports"
              value={completedCases}
              icon="CheckCircle"
              trendValue="+1 this week"
              trendUp={true}
            />
            <MetricsCard
              title="In Progress"
              value={inProgressCases}
              icon="Clock"
              trendValue="2 pending clarification"
              trendUp={false}
            />
            <MetricsCard
              title="Credits Remaining"
              value={totalCredits}
              icon="CreditCard"
              trendValue={subscription?.plan_type === 'free_trial' ? 'Free trial' : 'Active subscription'}
              trendUp={true}
            />
          </div>

          <QuickActions />

          {/* Analytics & Subscription */}
          <div>
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-4">Overview</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <UsageChart data={(() => {
                  // Build last-6-months buckets
                  const months = [];
                  for (let i = 5; i >= 0; i--) {
                    const d = new Date();
                    d.setDate(1);
                    d.setMonth(d.getMonth() - i);
                    months.push({
                      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
                      month: d.toLocaleString('en-US', { month: 'short', year: '2-digit' }),
                      cases: 0,
                    });
                  }
                  cases?.forEach((c) => {
                    const d = new Date(c.created_at);
                    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                    const bucket = months.find((m) => m.key === key);
                    if (bucket) bucket.cases += 1;
                  });
                  return months.map(({ month, cases }) => ({ month, cases }));
                })()} />
              </div>
              {subscriptionData && <SubscriptionPanel subscription={subscriptionData} userId={user?.id} />}
            </div>
          </div>


          {/* Recent Cases */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Recent Cases</h2>
              <button
                onClick={() => navigate('/client-dashboard/activity')}
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                View all activity
                <Icon name="ArrowRight" size={14} />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {formattedCases?.slice(0, 6)?.map((caseItem) => (
                <CaseCard key={caseItem?.caseId} caseData={caseItem} />
              ))}
              {(!formattedCases || formattedCases.length === 0) && (
                <div className="col-span-full text-center py-12">
                  <Icon name="FileText" size={40} className="text-muted-foreground mx-auto mb-3 opacity-40" />
                  <p className="text-muted-foreground text-sm">No cases yet. Create your first case to get started.</p>
                  <button
                    className="mt-4 text-sm text-primary hover:underline"
                    onClick={() => navigate('/new-case-creation-wizard')}
                  >
                    Create New Case
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 md:p-6">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <Icon name="AlertCircle" size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-base text-foreground mb-1">
                    Important: Revision Policy
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Any revision to a submitted case counts as a new case credit. Please ensure all information is accurate before submission.
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                iconName="Info"
                iconPosition="left"
                onClick={() => navigate('/landing-page')}
                className="flex-shrink-0"
              >
                Learn More
              </Button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ClientDashboard;